/**
 * EventEditModal Component - Google Calendar-style full-screen event editor
 *
 * Features:
 * - Full-screen edit view with close button (X)
 * - Inline editing of event fields
 * - Save and Cancel actions
 * - Follows Google Calendar UX patterns
 *
 * @example
 * <EventEditModal
 *   event={event}
 *   onClose={() => {}}
 *   onSave={() => {}}
 * />
 */

import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { X, Clock, MapPin, FileText, Tag, Users, Calendar as CalendarIcon } from 'lucide-react';
import { useCalendar } from '../../contexts/CalendarContext';
import calendarService from '../../services/calendarService';
import { toLocalInputString, toUTCString } from '../../utils/datetime';
import { getDefaultEventColor } from '../../utils/colorResolver';
import Button from '../Button';
import LoadingSpinner from '../LoadingSpinner';
import styles from './EventEditModal.module.css';

const EventEditModal = ({ event, onClose, onSave }) => {
  const { availableCalendars } = useCalendar();

  // Form state
  const [formData, setFormData] = useState({
    title: event.title || '',
    description: event.description || '',
    event_type: event.event_type || 'practice',
    visibility_roles: event.visibility_roles || ['all'],
    start_time: toLocalInputString(new Date(event.start_time)),
    end_time: toLocalInputString(new Date(event.end_time)),
    location_name: event.location || event.location_name || '',
    max_participants: event.max_participants || '',
    color_code: event.color_code || getDefaultEventColor()
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  // Event type options
  const eventTypes = [
    { value: 'practice', label: 'Practice' },
    { value: 'training_session', label: 'Training Session' },
    { value: 'skill_session', label: 'Skill Session' },
    { value: 'fitness_session', label: 'Fitness Session' },
    { value: 'game', label: 'Game' },
    { value: 'tournament', label: 'Tournament' },
    { value: 'scrimmage', label: 'Scrimmage' },
    { value: 'friendly_match', label: 'Friendly Match' },
    { value: 'team_meeting', label: 'Team Meeting' },
    { value: 'parent_meeting', label: 'Parent Meeting' },
    { value: 'coach_meeting', label: 'Coach Meeting' },
    { value: 'club_meeting', label: 'Club Meeting' },
    { value: 'camp', label: 'Camp' },
    { value: 'clinic', label: 'Clinic' },
    { value: 'fundraiser', label: 'Fundraiser' },
    { value: 'social_event', label: 'Social Event' },
    { value: 'custom', label: 'Custom Event' }
  ];

  // Visibility role options
  const visibilityRoles = [
    { value: 'all', label: 'All Members' },
    { value: 'staff', label: 'Staff Only' }
  ];

  // Track changes
  useEffect(() => {
    const changed =
      formData.title !== event.title ||
      formData.description !== (event.description || '') ||
      formData.event_type !== event.event_type ||
      formData.location_name !== (event.location || event.location_name || '') ||
      formData.start_time !== toLocalInputString(new Date(event.start_time)) ||
      formData.end_time !== toLocalInputString(new Date(event.end_time));

    setHasChanges(changed);
  }, [formData, event]);

  // Validate form
  const validate = () => {
    const newErrors = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Event title is required';
    }

    if (!formData.start_time) {
      newErrors.start_time = 'Start time is required';
    }

    if (!formData.end_time) {
      newErrors.end_time = 'End time is required';
    }

    if (formData.start_time && formData.end_time && formData.start_time >= formData.end_time) {
      newErrors.end_time = 'End time must be after start time';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (loading) return;

    if (!validate()) {
      return;
    }

    setLoading(true);

    try {
      const updateData = {
        title: formData.title.trim(),
        description: formData.description.trim() || null,
        event_type: formData.event_type,
        visibility_roles: formData.visibility_roles,
        start_time: toUTCString(formData.start_time),
        end_time: toUTCString(formData.end_time),
        location_name: formData.location_name.trim() || null,
        max_participants: formData.max_participants ? parseInt(formData.max_participants) : null,
        color_code: formData.color_code
      };

      await calendarService.updateEvent(event.id, updateData);

      // Success - call onSave callback
      if (onSave) {
        onSave();
      }

      // Close modal
      onClose();
    } catch (err) {
      console.error('Error updating event:', err);
      alert('Failed to update event. Please try again.');
      setLoading(false);
    }
  };

  // Handle field changes
  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  // Handle keyboard events
  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      if (hasChanges) {
        if (window.confirm('You have unsaved changes. Are you sure you want to close?')) {
          onClose();
        }
      } else {
        onClose();
      }
    }
  };

  return (
    <div className={styles.fullscreenUoverlay} onKeyDown={handleKeyDown}>
      <div className={styles.fullscreenUmodal}>
        {/* Header */}
        <div className={styles.modalUheader}>
          <div className={styles.headerUleft}>
            <h2 className={styles.headerUtitle}>Edit Event</h2>
          </div>
          <button
            className={styles.closeUbutton}
            onClick={onClose}
            aria-label="Close"
            type="button"
          >
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className={styles.modalUcontent}>
          {/* Event Title */}
          <div className={styles.formUgroup}>
            <label htmlFor="title" className={styles.formUlabel}>
              Event Title <span className={styles.required}>*</span>
            </label>
            <input
              type="text"
              id="title"
              value={formData.title}
              onChange={(e) => handleChange('title', e.target.value)}
              placeholder="Add title"
              className={`${styles.formUinput} ${errors.title ? styles.error : ''}`}
              autoFocus
            />
            {errors.title && <span className={styles.errorUtext}>{errors.title}</span>}
          </div>

          {/* Date and Time */}
          <div className={styles.formUrow}>
            <div className={styles.formUgroup}>
              <label htmlFor="start_time" className={styles.formUlabel}>
                <Clock size={16} className={styles.labelUicon} />
                Start Time <span className={styles.required}>*</span>
              </label>
              <input
                type="datetime-local"
                id="start_time"
                value={formData.start_time}
                onChange={(e) => handleChange('start_time', e.target.value)}
                className={`${styles.formUinput} ${errors.start_time ? styles.error : ''}`}
              />
              {errors.start_time && <span className={styles.errorUtext}>{errors.start_time}</span>}
            </div>

            <div className={styles.formUgroup}>
              <label htmlFor="end_time" className={styles.formUlabel}>
                <Clock size={16} className={styles.labelUicon} />
                End Time <span className={styles.required}>*</span>
              </label>
              <input
                type="datetime-local"
                id="end_time"
                value={formData.end_time}
                onChange={(e) => handleChange('end_time', e.target.value)}
                className={`${styles.formUinput} ${errors.end_time ? styles.error : ''}`}
              />
              {errors.end_time && <span className={styles.errorUtext}>{errors.end_time}</span>}
            </div>
          </div>

          {/* Event Type */}
          <div className={styles.formUgroup}>
            <label htmlFor="event_type" className={styles.formUlabel}>
              <Tag size={16} className={styles.labelUicon} />
              Event Type
            </label>
            <select
              id="event_type"
              value={formData.event_type}
              onChange={(e) => handleChange('event_type', e.target.value)}
              className={styles.formUselect}
            >
              {eventTypes.map(type => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>

          {/* Description */}
          <div className={styles.formUgroup}>
            <label htmlFor="description" className={styles.formUlabel}>
              <FileText size={16} className={styles.labelUicon} />
              Description
            </label>
            <textarea
              id="description"
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              placeholder="Add description"
              rows={4}
              className={styles.formUtextarea}
            />
          </div>

          {/* Location */}
          <div className={styles.formUgroup}>
            <label htmlFor="location_name" className={styles.formUlabel}>
              <MapPin size={16} className={styles.labelUicon} />
              Location
            </label>
            <input
              type="text"
              id="location_name"
              value={formData.location_name}
              onChange={(e) => handleChange('location_name', e.target.value)}
              placeholder="Add location"
              className={styles.formUinput}
            />
          </div>

          {/* Max Participants */}
          <div className={styles.formUgroup}>
            <label htmlFor="max_participants" className={styles.formUlabel}>
              <Users size={16} className={styles.labelUicon} />
              Max Participants
            </label>
            <input
              type="number"
              id="max_participants"
              value={formData.max_participants}
              onChange={(e) => handleChange('max_participants', e.target.value)}
              placeholder="No limit"
              min="1"
              className={styles.formUinput}
            />
          </div>

          {/* Event Color */}
          <div className={styles.formUgroup}>
            <label htmlFor="color_code" className={styles.formUlabel}>
              Event Color
            </label>
            <input
              type="color"
              id="color_code"
              value={formData.color_code}
              onChange={(e) => handleChange('color_code', e.target.value)}
              className={styles.colorUpicker}
            />
          </div>
        </form>

        {/* Footer Actions */}
        <div className={styles.modalUfooter}>
          <Button
            variant="ghost"
            onClick={onClose}
            disabled={loading}
            size="md"
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={loading || !hasChanges}
            size="md"
          >
            {loading ? (
              <>
                <LoadingSpinner size="sm" />
                Saving...
              </>
            ) : (
              'Save'
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default EventEditModal;
